import { Box, makeStyles, useTheme, useMediaQuery } from '@material-ui/core';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { PAGE_SIZE_LIST, STATUS_API } from 'src/app/constant/config';
import { _convertObjectToQuery } from 'src/app/utils/apiService';
import {
  getAgencyConfig,
  getListAgencies,
  getListAgencyBusiness
} from 'src/features/agencySlice';
import {
  getListDevice,
  getListDeviceAgency,
  getListDevicesForDriver,
  getListDeviceStaff,
  resetListDevice
} from 'src/features/deviceSlice';
import Results from './Results';
import Toolbar from './ToolBar';

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

const flattenAgencies = (agencies, parentName = '') => {
  const result = [];
  (agencies || []).forEach(agency => {
    result.push({ ...agency, parentName });
    if (agency.children?.length) {
      result.push(...flattenAgencies(agency.children, agency.name));
    }
  });
  return result;
};

const DeviceListView = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'), { noSsr: true });
  const query = useQuery();

  const totalDevice = useSelector(state => state.deviceSlice.totalDevice);
  const isLoading = useSelector(state => state.deviceSlice.isLoading);

  const statusCreate = useSelector(state => state.deviceSlice.statusCreate);
  const statusDelete = useSelector(state => state.deviceSlice.statusDelete);
  const statusDeleteMulti = useSelector(
    state => state.deviceSlice.statusDeleteMulti
  );
  const statusUpdate = useSelector(state => state.deviceSlice.statusUpdate);

  const statusActive = useSelector(state => state.deviceSlice.statusActive);
  const statusRecall = useSelector(state => state.deviceSlice.statusRecall);
  const statusTransfer = useSelector(state => state.deviceSlice.statusTransfer);
  const statusImport = useSelector(state => state.deviceSlice.statusImport);
  const statusTransferFromFile = useSelector(state => state.deviceSlice.statusTransferFromFile);
  const statusExtend = useSelector(state => state.deviceSlice.statusExtend);
  const statusTransmit = useSelector(state => state.deviceSlice.statusTransmit);
  const statusControlSound = useSelector(state => state.deviceSlice.statusControlSound);
  const statusUpdateLicensePlate = useSelector(state => state.deviceSlice.statusUpdateLicensePlate);
  const statusextendService = useSelector(state => state.deviceSlice.statusextendService);
  const statusControlAcc = useSelector(state => state.deviceSlice.statusControlAcc);
  const isAdmin = useSelector(state => state.authSlice.isAdmin);
  const isAgency = useSelector(state => state.authSlice.isAgency);
  const isUser = useSelector(state => state.authSlice.isUser);
  const isStaff = useSelector(state => state.authSlice.isStaff);
  const isAccountant = useSelector(state => state.authSlice.isAccountant);
  const isSale = useSelector(state => state.authSlice.isSale);
  const isLeadMarketing = useSelector(state => state.authSlice.isLeadMarketing);
  const isCustomerCare = useSelector(state => state.authSlice.isCustomerCare);
  const isTechnician = useSelector(state => state.authSlice.isTechnician);
  const isDriver = useSelector(state => state.authSlice.isDriver);
  const agencyId = useSelector(state => state.authSlice.agencyId);
  const listDevice = useSelector(state => state.deviceSlice.listDevice);
  const listAgencies = useSelector(state => state.agencySlice.listAgencies);
  const flatListAgencies = useMemo(
    () => (isAdmin ? flattenAgencies(listAgencies) : listAgencies),
    [isAdmin, listAgencies]
  );

  const [params, setParams] = useState({
    page: query.get('page') || 1,
    page_size: isMobile ? 6 : query.get('page_size') || PAGE_SIZE_LIST,
    agency_id: query.get('agency_id') || undefined,
    device_type_id: query.get('device_type_id') || undefined,
    status: query.get('status') || undefined,
    sub: query.get('sub') || undefined,
    sort_field: query.get('sort_field') || undefined,
    sort_order: query.get('sort_order') || undefined
  });
  
  const canFetchList = !isCustomerCare || Boolean(params.keywords);

  // Đại lý: nạp cấu hình để biết có quyền thay thế thiết bị bảo hành (is_allow_swap) không
  useEffect(() => {
    if (isAgency && agencyId) dispatch(getAgencyConfig(agencyId));
  }, [dispatch, isAgency, agencyId]);

  useEffect(() => {
    if (isAdmin || isAccountant || isSale || isLeadMarketing || isCustomerCare || isTechnician) {
      if (canFetchList) {
        dispatch(getListDevice(params));
      } else {
        dispatch(resetListDevice());
      }
    } else if (isAgency) {
      const dataRq = Object.assign({}, params);
      dispatch(
        getListDeviceAgency({
          data: dataRq,
          id: agencyId
        })
      );
    } else if (isUser) {
      dispatch(getListDevice(params));
    } else if (isStaff) {
      dispatch(getListDeviceStaff(params));
    } else if (isDriver) {
      dispatch(getListDevicesForDriver(params));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, isAgency, isUser, isStaff, isDriver, isAccountant, isSale, isLeadMarketing, isCustomerCare, isTechnician, canFetchList]);

  useEffect(() => {
    if (isAdmin || isAgency || isAccountant || isSale || isLeadMarketing || isCustomerCare || isTechnician) {
      dispatch(getListAgencies({ page_size: 1000 }));
    }
  }, [dispatch, isAdmin, isAgency, isAccountant, isSale, isLeadMarketing, isCustomerCare, isTechnician]);

  useEffect(() => {
    dispatch(getListAgencyBusiness());
  }, [dispatch]);

  const statuses = [
    statusCreate,
    statusDelete,
    statusDeleteMulti,
    statusUpdate,
    statusActive,
    statusRecall,
    statusTransfer,
    statusExtend,
    statusImport,
    statusTransmit,
    statusControlSound,
    statusTransferFromFile,
    statusUpdateLicensePlate,
    statusextendService,
    statusControlAcc
  ];

  const prevStatusesRef = useRef(statuses);
  useEffect(() => {
    const prevStatuses = prevStatusesRef.current;
    prevStatusesRef.current = statuses;
    const hasNewSuccess = statuses.some(
      (status, index) =>
        status === STATUS_API.SUCCESS && prevStatuses[index] !== STATUS_API.SUCCESS
    );
    if (hasNewSuccess) {
      if (isCustomerCare && !canFetchList) return;
      const action = getDeviceListAction();
      dispatch(action);
    }
  }, [
    statusCreate,
    statusDelete,
    statusDeleteMulti,
    statusUpdate,
    statusActive,
    statusRecall,
    statusTransfer,
    statusExtend,
    statusImport,
    statusTransmit,
    statusControlSound,
    statusTransferFromFile,
    statusUpdateLicensePlate,
    statusextendService,
    statusControlAcc,
    canFetchList
  ]);

  const getDeviceListAction = () => {
    if (isAgency) {
      return getListDeviceAgency({ data: params, id: agencyId });
    }
    if (isStaff) {
      return getListDeviceStaff(params);
    }
    if (isAdmin || isAccountant || isSale || isLeadMarketing || isCustomerCare || isTechnician) {
      return getListDevice(params);
    }
    if (isUser) {
      return getListDevice(params);
    }
    if (isDriver) {
      return getListDevicesForDriver(params);
    }
  };

  const getListDeviceWithParams = data => {
    const paramValue = Object.assign({}, params, data);
    setParams(paramValue);

    const queryString = _convertObjectToQuery(paramValue);
    navigate(`/app/manage/devices?${queryString}`);

    if (isAgency) {
      dispatch(
        getListDeviceAgency({
          data: paramValue,
          id: agencyId
        })
      );
    } else if (isAdmin || isAccountant || isSale || isLeadMarketing || isCustomerCare || isTechnician) {
      if (isCustomerCare && !paramValue.keywords) {
        dispatch(resetListDevice());
      } else {
        dispatch(getListDevice(paramValue));
      }
    } else if (isUser) {
      dispatch(getListDevice(paramValue));
    } else if (isStaff) {
      dispatch(getListDeviceStaff(paramValue));
    } else if (isDriver) {
      dispatch(getListDevicesForDriver(paramValue));
    }
  };
  return (
    <>
      <div className={classes.root}>
        <div style={{ width: '98%', margin: '0 auto' }}>
          <Toolbar
            isLoading={isLoading}
            listAgencies={flatListAgencies}
            searchRef={getListDeviceWithParams}
          />
          <Box mt={2}>
            <Results
              listDevice={listDevice}
              totalDevice={totalDevice}
              isLoading={isLoading}
              getListDeviceRef={getListDeviceWithParams}
              currentParams={params}
            />
          </Box>
        </div>
      </div>
    </>
  );
};

const useStyles = makeStyles(theme => ({
  root: {
    backgroundColor: theme.palette.background.dark,
    display: 'flex',
    width: '100%',
    paddingTop: theme.spacing(2)
  }
}));

export default DeviceListView;
